import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

interface PublicContract {
  id: number;
  title: string;
  content: string;
  status: string;
  signature_required: boolean;
  expiry_date?: string;
  deal_title?: string;
  lead_email?: string;
}

const PublicContractSigning: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [contract, setContract] = useState<PublicContract | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [signing, setSigning] = useState(false);
  const [signed, setSigned] = useState(false);
  const [signatureData, setSignatureData] = useState({
    signed_by_name: '',
    signed_by_email: '',
    signature_data: ''
  });
  const [hasSignature, setHasSignature] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);

  useEffect(() => {
    if (token) {
      fetchContract();
    } else {
      setError('Invalid contract link');
      setLoading(false);
    }
  }, [token]);

  const fetchContract = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/contracts/public/${token}`);
      setContract(response.data);
      
      // Pre-fill email if available
      if (response.data.lead_email) {
        setSignatureData(prev => ({
          ...prev,
          signed_by_email: response.data.lead_email
        }));
      }
    } catch (error: any) {
      console.error('Error fetching contract:', error);
      if (error.response?.status === 404) {
        setError('Contract not found or has expired');
      } else if (error.response?.status === 410) {
        setError('This contract has expired');
      } else {
        setError('Failed to load contract');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setSignatureData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Signature canvas functions
  const getCoordinates = (event: React.MouseEvent | React.TouchEvent) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };

    const rect = canvas.getBoundingClientRect();
    const clientX = 'touches' in event ? event.touches[0].clientX : event.clientX;
    const clientY = 'touches' in event ? event.touches[0].clientY : event.clientY;

    // Account for canvas scaling - the canvas has a fixed width/height but CSS scales it
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY,
    };
  };

  const startDrawing = (event: React.MouseEvent | React.TouchEvent) => {
    event.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const { x, y } = getCoordinates(event);
    
    setIsDrawing(true);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = (event: React.MouseEvent | React.TouchEvent) => {
    event.preventDefault();
    if (!isDrawing) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const { x, y } = getCoordinates(event);
    
    ctx.lineTo(x, y);
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.stroke();

    setHasSignature(true);
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
    setSignatureData(prev => ({ ...prev, signature_data: '' }));
  };

  const saveSignature = () => {
    const canvas = canvasRef.current;
    if (!canvas) return '';

    return canvas.toDataURL('image/png');
  };

  const handleSign = async () => {
    if (!signatureData.signed_by_name.trim() || !signatureData.signed_by_email.trim()) {
      setError('Please enter your full name and email address');
      return;
    }

    if (!hasSignature) {
      setError('Please provide your signature by drawing in the signature box');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(signatureData.signed_by_email)) {
      setError('Please enter a valid email address');
      return;
    }

    setSigning(true);
    setError(null);

    try {
      const signatureImage = saveSignature();
      await axios.post(`/api/contracts/public/${token}/sign`, {
        signed_by_name: signatureData.signed_by_name.trim(),
        signed_by_email: signatureData.signed_by_email.trim(),
        signature_data: signatureImage
      });

      setSigned(true);
      // Refresh contract to show updated status
      await fetchContract();
    } catch (error: any) {
      console.error('Error signing contract:', error);
      if (error.response?.status === 400) {
        setError(error.response.data.message || 'Cannot sign this contract');
      } else if (error.response?.status === 410) {
        setError('This contract has expired');
      } else {
        setError('Failed to sign contract. Please try again.');
      }
    } finally {
      setSigning(false);
    }
  };

  const isExpired = contract?.expiry_date && new Date(contract.expiry_date) < new Date();
  const canSign = contract?.status === 'sent' && !isExpired;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading contract...</p>
        </div>
      </div>
    );
  }

  if (error && !contract) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-6 text-center">
          <div className="text-red-600 mb-4">
            <svg className="h-12 w-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.962-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h1 className="text-xl font-semibold text-gray-900 mb-2">Contract Not Available</h1>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  if (signed || contract?.status === 'signed') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-6 text-center">
          <div className="text-green-600 mb-4">
            <svg className="h-12 w-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h1 className="text-xl font-semibold text-gray-900 mb-2">Contract Signed Successfully!</h1>
          <p className="text-gray-600 mb-4">
            Thank you for signing the contract. A copy has been sent to your email address.
          </p>
          <div className="text-sm text-gray-500">
            <p><strong>Contract:</strong> {contract?.title}</p>
            <p><strong>Signed by:</strong> {signatureData.signed_by_name || 'You'}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Contract Review & Signature</h1>
          <div className="text-sm text-gray-600">
            <p><strong>Contract:</strong> {contract?.title}</p>
            {contract?.deal_title && <p><strong>Related to:</strong> {contract.deal_title}</p>}
            {contract?.expiry_date && (
              <p><strong>Expires:</strong> {new Date(contract.expiry_date).toLocaleDateString()}</p>
            )}
          </div>
          
          {isExpired && (
            <div className="mt-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
              <strong>This contract has expired and can no longer be signed.</strong>
            </div>
          )}
          
          {contract?.status === 'rejected' && (
            <div className="mt-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
              <strong>This contract has been rejected.</strong>
            </div>
          )}
        </div>

        {/* Contract Content */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Contract Terms</h2>
          <div 
            className="prose max-w-none text-gray-700 border border-gray-200 rounded p-4 bg-gray-50"
            style={{ minHeight: '400px' }}
            dangerouslySetInnerHTML={{ __html: contract?.content || '' }}
          />
        </div>

        {/* Signature Section */}
        {canSign && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Digital Signature</h2>
            
            {error && (
              <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                {error}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="signed_by_name"
                  value={signatureData.signed_by_name}
                  onChange={handleInputChange}
                  placeholder="Enter your full legal name"
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  name="signed_by_email"
                  value={signatureData.signed_by_email}
                  onChange={handleInputChange}
                  placeholder="Enter your email address"
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
            </div>

            {/* Signature Canvas */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Digital Signature <span className="text-red-500">*</span>
              </label>
              <div className="border-2 border-gray-300 rounded-md bg-white" style={{ width: '100%', height: '150px' }}>
                <canvas
                  ref={canvasRef}
                  width={600}
                  height={150}
                  className="cursor-crosshair"
                  onMouseDown={startDrawing}
                  onMouseMove={draw}
                  onMouseUp={stopDrawing}
                  onMouseLeave={stopDrawing}
                  onTouchStart={startDrawing}
                  onTouchMove={draw}
                  onTouchEnd={stopDrawing}
                  style={{ 
                    touchAction: 'none',
                    width: '100%',
                    height: '150px',
                    display: 'block'
                  }}
                />
              </div>
              <div className="flex justify-between items-center mt-2">
                <p className="text-xs text-gray-500">Draw your signature above</p>
                <button
                  type="button"
                  onClick={clearSignature}
                  className="text-xs text-gray-600 hover:text-gray-800 underline"
                >
                  Clear Signature
                </button>
              </div>
            </div>

            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded">
              <p className="text-sm text-blue-800">
                <strong>Digital Signature Notice:</strong> By providing your signature and clicking "Sign Contract" below, you agree to be legally bound by the terms and conditions outlined in this contract. Your digital signature has the same legal effect as a handwritten signature.
              </p>
            </div>

            <div className="flex justify-between items-center">
              <div className="text-sm text-gray-500">
                <p>Contract ID: {contract?.id}</p>
                <p>Today's date: {new Date().toLocaleDateString()}</p>
              </div>
              
              <button
                onClick={handleSign}
                disabled={signing || !signatureData.signed_by_name.trim() || !signatureData.signed_by_email.trim() || !hasSignature}
                className="bg-green-600 text-white px-6 py-3 rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
              >
                {signing ? 'Signing Contract...' : 'Sign Contract'}
              </button>
            </div>
          </div>
        )}

        {!canSign && contract && (
          <div className="bg-white rounded-lg shadow-md p-6 text-center">
            <p className="text-gray-600">
              {contract.status === 'draft' ? 'This contract is still in draft mode.' :
               contract.status === 'rejected' ? 'This contract has been rejected.' :
               isExpired ? 'This contract has expired.' :
               'This contract is not available for signing.'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PublicContractSigning;